import type {
  ConjointDesign,
  InterviewItem,
  InterviewItemTemplate,
} from "../types";
import { generateConjointDesign } from "./generateDesign";

export {
  generateConjointDesign,
  computeProfileSpaceSize,
} from "./generateDesign";

// "None of these" is stored as the chosen profile index, so the sentinel has to
// live where both the interview UI and the answer reducers can read it.
export const CONJOINT_NONE_INDEX = -1;

// A price level's number, or null when the label is not purely one. Both the
// linear-coding gate in the editor and willingness to pay read prices out of
// level text, so they have to agree on what counts as a price: keeping "-"
// means "12-month plan" and "$40 - $60" fail to parse instead of quietly
// reading as 12 and 4060.
export const parseConjointPriceLevel = (text: string): number | null => {
  const cleaned = text.replace(/[^0-9.-]/g, "");
  // Require a digit, since Number("") is 0 and would let a wordy label through.
  if (!/\d/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};

export const DEFAULT_CONJOINT_PROFILES_PER_SCREEN = 3;
export const DEFAULT_CONJOINT_NUM_TASKS = 10;

const getProfilesPerScreen = (
  item: Pick<InterviewItem | InterviewItemTemplate, "profilesPerScreen">
): number => item.profilesPerScreen ?? DEFAULT_CONJOINT_PROFILES_PER_SCREEN;

const getNumTasks = (
  item: Pick<InterviewItem | InterviewItemTemplate, "numTasks">
): number => item.numTasks ?? DEFAULT_CONJOINT_NUM_TASKS;

// Same FNV-1a seed hash used by the MaxDiff builder so per-respondent designs
// are deterministic and reproducible from a stable seed base.
const hashSeed = (seed: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index++) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

export const getConjointTotalTasks = (
  item: InterviewItem | InterviewItemTemplate
): number => {
  if ((item.attributes ?? []).filter((a) => a.levels.length > 0).length === 0)
    return 0;
  return getNumTasks(item);
};

export const buildConjointDesign = (
  item: InterviewItem | InterviewItemTemplate,
  seedBase?: string
): ConjointDesign =>
  generateConjointDesign({
    attributes: item.attributes ?? [],
    profilesPerScreen: getProfilesPerScreen(item),
    numTasks: getNumTasks(item),
    seed: hashSeed(`${seedBase ?? "preview"}:${item.id}`),
  });
